import type { Products } from '@strim/gateway-api';
import API from '@strim/gateway-api';

import { getProductsPriceAndDiscount } from '../../domain/products/utils/productHelpers';

import {
  Voucher,
  VoucherDiscounts,
  VoucherPricePercentDiscount,
  VoucherPriceReductionDiscount,
} from './forces/voucher.types';

const VOUCHER_KEY = 'preloadedVoucher';

interface SerializedVoucher {
  code: string;
  expiry: number;
}

export const checkForPreloadedVoucher = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const voucher = urlParams.get('vc');

  if (voucher && voucher.length > 0) {
    setVoucherToLocalStorage(voucher);
  }
};

export const setVoucherToLocalStorage = (code?: string) => {
  if (!code) {
    localStorage.removeItem(VOUCHER_KEY);
    return;
  }
  const voucher = {
    expiry: new Date(Date.now() + 24 * 3600 * 1000).getTime(),
    code,
  };
  localStorage.setItem(VOUCHER_KEY, JSON.stringify(voucher));
};

export const getVoucherFromLocalStorage = (): string | undefined => {
  try {
    const rawValue = localStorage.getItem(VOUCHER_KEY);
    if (rawValue) {
      const voucher: SerializedVoucher = JSON.parse(rawValue);
      if (voucher.expiry > new Date().getTime()) {
        return voucher.code;
      } else {
        localStorage.removeItem(VOUCHER_KEY);
      }
    }
  } catch (err) {
    localStorage.removeItem(VOUCHER_KEY);
  }
};

export const hasDiscountType = (voucher: Pick<Voucher, 'discounts'> | undefined, type: API.DiscountType): boolean => {
  return voucher?.discounts?.map(d => d.discountType)?.includes(type) ?? false;
};

export const hasOnlyNoDiscount = (voucher?: Voucher): boolean => {
  return voucher?.discounts?.map(d => d.discountType)?.length === 1 && hasDiscountType(voucher, 'NoDiscount');
};

export const hasOnlyTrialDiscount = (voucher?: Voucher): boolean => {
  return voucher?.discounts?.map(d => d.discountType)?.length === 1 && hasDiscountType(voucher, 'NewTrialLength');
};

/**
 * Returns valid reduced price and percent from parsed price voucher description.
 * Returns null when there are any global campaigns on products
 */
export const getPricePercentVoucherDiscount = (voucher: Voucher | undefined, productsToBuy: Products.Product[]) => {
  const priceVoucherDetails = voucher?.discounts?.find(isPriceVoucherDiscount);
  if (!priceVoucherDetails) {
    return null;
  }
  const baseProduct = productsToBuy.find(p => p.baseProduct);
  if (!baseProduct) {
    return null;
  }
  // only apply voucher discount when no global campaign on (base) products
  const { totalDiscountAmount: globalDiscount } = getProductsPriceAndDiscount(productsToBuy);
  if (globalDiscount) {
    return;
  }

  // Yay we have a valid price voucher
  return {
    /** Price in NOK */
    price: baseProduct.price,
    /** Reduced amount in NOK */
    reducedAmount: parseFloat((baseProduct.price * (priceVoucherDetails.percent / 100)).toFixed(2)),
    /** Percent is an int on the range 1-100 */
    percent: priceVoucherDetails.percent,
    numberOfBillingCycles: priceVoucherDetails.durationMonths,
    productId: baseProduct.id,
  };
};

const isPriceVoucherDiscount = (discount: VoucherDiscounts): discount is VoucherPricePercentDiscount => {
  return discount.discountType === 'PricePercentFactor';
};

/**
 * Returns valid reduced price and amount from a flat price reduction voucher.
 * Returns null when there are any global campaigns on products
 */
export const getPriceReductionVoucherDiscount = (voucher: Voucher | undefined, productsToBuy: Products.Product[]) => {
  const priceReductionDetails = voucher?.discounts?.find(isPriceReductionVoucherDiscount);
  if (!priceReductionDetails) {
    return null;
  }
  const product = productsToBuy.find(p => p.id === priceReductionDetails.productId);
  if (!product) {
    return null;
  }
  // only apply voucher discount when no global campaign on (base) products
  const { totalDiscountAmount: globalDiscount } = getProductsPriceAndDiscount(productsToBuy);
  if (globalDiscount) {
    return;
  }

  // A voucher can contain several price-reduction discounts on the same product with different
  // durations. They stack during the first (shortest) period, so we sum them for the current amount.
  const discountsForProduct =
    voucher?.discounts?.filter(isPriceReductionVoucherDiscount).filter(d => d.productId === product.id) ?? [];
  const totalPriceReduction = discountsForProduct.reduce((total, d) => total + d.priceReduction, 0);
  const shortestDuration = discountsForProduct
    .map(d => d.durationMonths)
    .sort((a, b) => (a ?? Infinity) - (b ?? Infinity))[0];

  return {
    /** Price in NOK */
    price: product.price,
    /** Reduced amount in NOK */
    reducedAmount: Math.min(totalPriceReduction, product.price),
    percent: undefined,
    numberOfBillingCycles: shortestDuration,
    productId: product.id,
    isMultipleDiscounts: discountsForProduct.length > 1,
  };
};

const isPriceReductionVoucherDiscount = (discount: VoucherDiscounts): discount is VoucherPriceReductionDiscount => {
  return discount.discountType === 'PriceReduction';
};

/**
 * Returns the product ids a price-reduction voucher applies to when none of those products are in
 * the current selection. Returns null when the voucher has no price-reduction discount, or when at
 * least one of its price-reduction discounts applies to a selected product.
 */
export const getUnappliedPriceReductionDiscount = (
  voucher: Voucher | undefined,
  productsToBuy: Products.Product[]
): { requiredProductIds: string[] } | null => {
  const priceReductionDiscounts = voucher?.discounts?.filter(isPriceReductionVoucherDiscount) ?? [];
  if (priceReductionDiscounts.length === 0) {
    return null;
  }
  const appliesToSelectedProduct = priceReductionDiscounts.some(d => productsToBuy.some(p => p.id === d.productId));
  if (appliesToSelectedProduct) {
    return null;
  }
  const requiredProductIds = [...new Set(priceReductionDiscounts.map(d => d.productId))];
  return { requiredProductIds };
};
